/**
 * Kafelki z GET /sfd-zamowienia/podsumowanie (razem, ostatnia synchronizacja, naglowki do pobrania)
 * + tabela "Per sklep" (liczba i brutto; klik = filtr sklepu) + legenda kodow statusow z backendu.
 */
import React from 'react';
import { formatujKwote, formatujDateCzas, kolorStatusu, NIEZNANY_ADRES, SKLEP_NIEZNANY_ID } from './format';

const Kafelek = ({ id, etykieta, wartosc, dopisek, kolor, tlo }) => (
  <div data-testid={`kafelek-${id}`} style={{ flex: '1 1 140px', minWidth: '140px', padding: '8px 10px', borderRadius: '6px', backgroundColor: tlo, borderLeft: `3px solid ${kolor}` }}>
    <div style={{ fontSize: '18px', fontWeight: 700, color: kolor, lineHeight: 1.2 }}>{wartosc}</div>
    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>{etykieta}</div>
    {dopisek && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{dopisek}</div>}
  </div>
);

const opisSync = (s) => {
  if (!s) return { wartosc: 'nigdy', dopisek: 'kliknij „Odśwież z SFD”', kolor: 'var(--text-muted)', tlo: 'var(--bg)' };
  if (s.status === 'w_toku') return { wartosc: 'trwa…', dopisek: `od ${formatujDateCzas(s.rozpoczeto)}`, kolor: 'var(--info)', tlo: 'var(--info-light)' };
  if (s.status === 'blad') return { wartosc: 'błąd', dopisek: s.komunikat || formatujDateCzas(s.zakonczono), kolor: 'var(--danger)', tlo: 'var(--danger-light)' };
  return { wartosc: formatujDateCzas(s.zakonczono || s.rozpoczeto), dopisek: `${s.pobrano ?? 0} zamówień, nowych ${s.nowych ?? 0}`, kolor: 'var(--success)', tlo: 'var(--success-light)' };
};

const Podsumowanie = ({ podsumowanie }) => {
  const p = podsumowanie || {};
  const razem = p.razem || {};
  const sync = opisSync(p.ostatnia_synchronizacja);
  const naglowki = p.naglowki_do_pobrania ?? 0;
  return (
    <div data-testid="podsumowanie-zamowien" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '12px' }}>
      <Kafelek id="razem" etykieta="Zamówień w zakresie" wartosc={razem.liczba ?? '—'} dopisek={`brutto ${formatujKwote(razem.brutto)} · netto ${formatujKwote(razem.netto)}`} kolor="var(--primary)" tlo="var(--primary-light)" />
      <Kafelek id="sync" etykieta="Ostatnia synchronizacja" wartosc={sync.wartosc} dopisek={sync.dopisek} kolor={sync.kolor} tlo={sync.tlo} />
      <Kafelek id="naglowki" etykieta="Bez nagłówka (sklep, uwagi)" wartosc={naglowki} dopisek={naglowki > 0 ? 'dociągnie kolejne odświeżenie' : 'wszystkie pobrane'} kolor={naglowki > 0 ? 'var(--warning)' : 'var(--text-muted)'} tlo={naglowki > 0 ? 'var(--warning-light)' : 'var(--bg)'} />
    </div>
  );
};

const th = { padding: '5px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' };
const td = { padding: '4px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px' };
const prawa = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

/** Tabela per sklep: [{sklep_id|null, nazwa, liczba, brutto, netto}]. Klik -> onWybierz(sklep_id | 0). */
export const PerSklep = ({ perSklep, aktywnySklepId, onWybierz }) => {
  const lista = Array.isArray(perSklep) ? perSklep : [];
  return (
    <div className="card" data-testid="per-sklep-zamowien" style={{ fontSize: '12px' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600 }}>Per sklep (wg adresu dostawy z SFD)</h3>
      {lista.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Brak danych — zamówienia pojawią się po odświeżeniu z SFD</div>
      ) : (
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Sklep</th>
                <th style={{ ...th, textAlign: 'right' }}>Zamówień</th>
                <th style={{ ...th, textAlign: 'right' }}>Netto</th>
                <th style={{ ...th, textAlign: 'right' }}>Brutto</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((w, idx) => {
                const nieznany = w.sklep_id === null || w.sklep_id === undefined;
                const id = nieznany ? SKLEP_NIEZNANY_ID : w.sklep_id;
                const aktywny = aktywnySklepId !== null && aktywnySklepId !== undefined && aktywnySklepId === id;
                return (
                  <tr key={`${id}-${idx}`} data-testid="per-sklep-wiersz" role="button" tabIndex={0}
                    title="Kliknij, żeby pokazać zamówienia tego sklepu"
                    onClick={() => onWybierz && onWybierz(id, w)}
                    onKeyDown={(e) => { if (onWybierz && (e.key === 'Enter' || e.key === ' ')) onWybierz(id, w); }}
                    style={{ cursor: 'pointer', backgroundColor: aktywny ? 'var(--primary-light)' : (nieznany ? 'var(--warning-light)' : 'transparent') }}>
                    <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap', color: nieznany ? 'var(--warning)' : 'var(--text)' }}>{nieznany ? NIEZNANY_ADRES : w.nazwa}</td>
                    <td style={prawa}>{w.liczba ?? '—'}</td>
                    <td style={prawa}>{formatujKwote(w.netto)}</td>
                    <td style={{ ...prawa, fontWeight: 600 }}>{formatujKwote(w.brutto)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/** Legenda kodow statusow (slownik z backendu; brak etykiety = "nieznany"). */
export const LegendaStatusow = ({ legenda }) => {
  const l = legenda || {};
  const wpisy = (slownik) => Object.entries(slownik || {}).map(([kod, etykieta]) => `${kod} = ${etykieta}`).join(', ');
  return (
    <div data-testid="legenda-statusow" style={{ fontSize: '10px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
      <span style={{ fontWeight: 600 }}>Kody z portalu:</span>{' '}
      status {wpisy(l.status) || '—'}; płatność {wpisy(l.platnosc) || '—'}; dostawa {wpisy(l.dostawa) || '—'};
      pozostałe kody = <span style={{ ...kolorStatusu(null), padding: '0 4px', borderRadius: '6px' }}>{l.nieznany || 'nieznany'}</span> (do uzupełnienia w jednym słowniku w backendzie).
    </div>
  );
};

export default Podsumowanie;
